'use client';

import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import { useSiteLanguage } from '@/lib/SiteLanguageContext';

const content = {
    sr: {
        heading: 'Naš tim',
        intro: (
            <>
                Iza <strong className="font-semibold">Studija odbrambenih sistema</strong> stoje ljudi koji kombinuju tehničku stručnost, vojnu ekspertizu i strast prema edukaciji. Upoznajte tim koji svakodnevno radi na tome da vam pruži precizan, detaljan i kvalitetan sadržaj.
            </>
        ),
        km: {
            role: 'Osnivač i urednik bloga',
            bio: (
                <>
                    Kristijan je kreator i pokretačka snaga iza <strong className="font-semibold">Studija odbrambenih sistema</strong>. On je osmislio, izgradio i od prvog dana neprekidno održava i unapređuje ovaj blog – od tehničke infrastrukture i dizajna, do uređivačke politike i organizacije sadržaja. Kristijan brine o tome da platforma radi besprijekorno, da je korisnički iskustvo na najvisočijem nivou i da se blog konstantno razvija u korak s potrebama čitalaca. Iza svakog tehničkog detalja, novog sadržaja i svakog ažuriranja stoji njegov trud i predanost ovom projektu.
                </>
            ),
        },
        sm: {
            role: 'Vojni ekspert i analitičar',
            bio: (
                <>
                    Savo je vojni ekspert čije znanje i iskustvo čine stručnu osnovu bloga. On doprinosi dubokim analitičkim uvidima u odbrambene sisteme, oružane tehnologije i vojnu istoriju, osiguravajući da svaki objavljeni sadržaj bude tehnički precizan, stručno argumentovan i utemeljen na provjerenim vojnim principima. Zahvaljujući njegovu doprinosu, <strong className="font-semibold">Studije odbrambenih sistema</strong> pružaju čitaocima perspektivu koja prevazilazi površnu interpretaciju – a ide ka suštinskom razumijevanju odbrambenih tema.
                </>
            ),
        },
    },
    en: {
        heading: 'Our Team',
        intro: (
            <>
                Behind <strong className="font-semibold">Defense Systems Studies</strong> stand people who combine technical expertise, military knowledge, and a passion for education. Meet the team that works every day to provide you with accurate, detailed, and high-quality content.
            </>
        ),
        km: {
            role: 'Founder and Blog Editor',
            bio: (
                <>
                    Kristijan is the creator and driving force behind <strong className="font-semibold">Defense Systems Studies</strong>. He conceived, built, and has continuously maintained and improved this blog since day one — from the technical infrastructure and design to editorial policy and content organization. Kristijan ensures the platform runs flawlessly, that the user experience is at the highest level, and that the blog constantly evolves in step with the needs of its readers. Behind every technical detail, new piece of content, and every update stands his dedication and commitment to this project.
                </>
            ),
        },
        sm: {
            role: 'Military Expert and Analyst',
            bio: (
                <>
                    Savo is a military expert whose knowledge and experience form the professional backbone of the blog. He contributes deep analytical insights into defense systems, weapons technologies, and military history, ensuring that every published piece of content is technically accurate, expertly argued, and grounded in verified military principles. Thanks to his contributions, <strong className="font-semibold">Defense Systems Studies</strong> offers readers a perspective that goes beyond surface-level interpretation — toward a genuine understanding of defense topics.
                </>
            ),
        },
    },
};

export default function TimPage() {
    const { siteLang } = useSiteLanguage();
    const t = siteLang === 'en' ? content.en : content.sr;

    return (
        <main className="min-h-screen flex flex-col">
            <Header />

            <section className="max-w-4xl mx-auto py-8 md:py-10 px-4 sm:px-6 lg:px-8 text-base lg:text-[18px]">
                <div className="flex items-center justify-center pt-0 sm:pt-1 pb-4 mb-6">
                    <Link href="/homepage" className="w-full text-center">
                        <div className="mx-auto w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36">
                            <Image src={siteLang === 'en' ? '/Logo_EN.png' : '/Logo_SR.png'} alt="logo" width={144} height={144} className="w-full h-full object-contain mx-auto" />
                        </div>
                        <div className="mt-3">
                            <span className="font-headline text-2xl md:text-4xl font-bold">
                                {siteLang === 'en' ? 'Studies of Defense Systems' : 'Studije odbrambenih sistema'}
                            </span>
                        </div>
                    </Link>
                </div>

                <h1 className="text-[20px] font-bold mb-4 text-left">{t.heading}</h1>
                <p className="mb-8 text-base leading-relaxed text-left">{t.intro}</p>

                {/* Kristijan Mitrović */}
                <div className="mb-10 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-2xl font-bold text-gray-500 dark:text-gray-300 select-none">
                            KM
                        </div>
                        <div>
                            <h2 className="text-lg font-bold mb-1">Kristijan Mitrović</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 font-medium uppercase tracking-wide">{t.km.role}</p>
                            <p className="text-base leading-relaxed text-left">{t.km.bio}</p>
                        </div>
                    </div>
                </div>

                {/* Savo Mitrović */}
                <div className="mb-10 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-2xl font-bold text-gray-500 dark:text-gray-300 select-none">
                            SM
                        </div>
                        <div>
                            <h2 className="text-lg font-bold mb-1">Savo Mitrović</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 font-medium uppercase tracking-wide">{t.sm.role}</p>
                            <p className="text-base leading-relaxed text-left">{t.sm.bio}</p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="mt-auto">
                <Footer />
            </div>
        </main>
    );
}
